// src/pages/admin/Reports.jsx
import React, { useState } from 'react';
import axios from 'axios';
import { Shield, Info, AlertCircle, Download, CheckCircle } from 'lucide-react';
import Alert from '../../components/ui/Alert';
import usePermissions from '../../hooks/usePermissions';

const API_URL = 'https://medigo-api.onrender.com/api';

const Reports = () => {
 
  const {
    canViewReports,
    canGenerateReports,
    isSuperAdmin,
    isAdmin
  } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [loadingReport, setLoadingReport] = useState(null); 
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [generatedReports, setGeneratedReports] = useState([]); 

  
  const generateReport = async (reportType) => {
    if (!canGenerateReports) {
      setError('You do not have permission to generate reports');
      return;
    }

    try {
      setLoading(true);
      setLoadingReport(reportType);
      const token = localStorage.getItem('medigo_token');
      
      if (!token) {
        setError('Not authenticated. Please login again.');
        setLoading(false);
        setLoadingReport(null);
        return;
      }

      const params = new URLSearchParams();
      if (dateRange.startDate) params.append('startDate', dateRange.startDate);
      if (dateRange.endDate) params.append('endDate', dateRange.endDate);

      const response = await axios.get(`${API_URL}/admin/reports/${reportType}?${params}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data) {
        if (response.data.fileUrl) {
          window.open(response.data.fileUrl, '_blank');
          setSuccess(`${reportType} report downloaded successfully!`);
        } 
        else if (response.data.data) {
          const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${reportType}-report-${new Date().toISOString().split('T')[0]}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          setSuccess(`${reportType} report generated and downloaded!`);
        }
        else {
          setSuccess(`${reportType} report generated successfully!`);
        }
        
        setGeneratedReports(prev => [...prev, {
          type: reportType,
          timestamp: new Date().toISOString(),
          dateRange: { ...dateRange }
        }]);
      }
      
    } catch (err) {
      console.error('❌ Error generating report:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      
      if (err.response?.status === 404) {
        setError(`Report endpoint not found. The API route for ${reportType} reports may not be implemented yet.`);
      } else if (err.response?.status === 403) {
        setError('Permission denied. You may not have access to generate this report.');
      } else if (err.response?.status === 401) {
        setError('Authentication failed. Please login again.');
      } else {
        setError(err.response?.data?.message || `Failed to generate ${reportType} report. ${err.message}`);
      }
    } finally {
      setLoading(false);
      setLoadingReport(null);
    }
  };

  const reportTypes = [
    {
      id: 'users',
      title: 'Users Report',
      description: 'Comprehensive user statistics and demographics',
      icon: '👥',
      color: 'blue'
    },
    {
      id: 'revenue',
      title: 'Revenue Report',
      description: 'Financial overview and payment analytics',
      icon: '💰',
      color: 'green'
    },
    {
      id: 'consultations',
      title: 'Consultations Report',
      description: 'Consultation statistics and trends',
      icon: '🏥',
      color: 'purple'
    },
    {
      id: 'orders',
      title: 'Orders Report',
      description: 'Medicine order analytics',
      icon: '📦',
      color: 'orange'
    }
  ];

  if (!canViewReports) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          Reports
          {isSuperAdmin && <Shield className="w-7 h-7 text-purple-600" />}
        </h1>
        <p className="text-gray-600 mt-1">Generate and download system reports</p>
      </div>

      {/* Permission Badge */}
      {isSuperAdmin ? (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center gap-3">
          <Shield className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div>
            <p className="text-purple-900 font-medium">Super Admin Privileges Active</p>
            <p className="text-purple-700 text-sm">
              Full access to all reports and analytics data
            </p>
          </div>
        </div>
      ) : isAdmin ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-blue-900 font-medium">Admin Access</p>
            <p className="text-blue-700 text-sm">
              You can generate and view all system reports
            </p>
          </div>
        </div>
      ) : null}

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      {/* Date Range Selector */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Date Range (Optional)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>
        {dateRange.startDate && dateRange.endDate && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              📅 Reports will be generated for: <strong>{dateRange.startDate}</strong> to <strong>{dateRange.endDate}</strong>
            </p>
          </div>
        )}
      </div>

      {/* Report Cards - FIXED: Changed report._id to report.id */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {reportTypes.map(report => (
          <div key={report.id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="text-4xl mb-4">{report.icon}</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{report.title}</h3>
            <p className="text-sm text-gray-600 mb-4 min-h-[40px]">{report.description}</p>
            
            {canGenerateReports ? (
              <button
                onClick={() => generateReport(report.id)}
                disabled={loading}
                className={`w-full px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
                  report.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
                  report.color === 'green' ? 'bg-green-600 hover:bg-green-700' :
                  report.color === 'purple' ? 'bg-purple-600 hover:bg-purple-700' :
                  'bg-orange-600 hover:bg-orange-700'
                }`}
              >
                {loadingReport === report.id ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Generate Report
                  </>
                )}
              </button>
            ) : (
              <button
                disabled
                className="w-full px-4 py-2 bg-gray-400 text-white rounded-lg cursor-not-allowed opacity-50"
                title="You don't have permission to generate reports"
              >
                No Permission
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      {canGenerateReports && (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold mb-2">Generate All Reports</h3>
              <p className="text-indigo-100">
                Generate all available reports at once for the selected date range
              </p>
            </div>
            <button
              onClick={async () => {
                for (const report of reportTypes) {
                  await generateReport(report.id);
                  await new Promise(resolve => setTimeout(resolve, 500));
                }
              }}
              disabled={loading}
              className="px-6 py-3 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50 font-medium"
            >
              {loading ? 'Generating...' : 'Generate All'}
            </button>
          </div>
        </div>
      )}

      {/* Recent Reports / Generated Reports */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {generatedReports.length > 0 ? 'Generated Reports (This Session)' : 'Recent Reports'}
          </h3>
        </div>
        
        {generatedReports.length > 0 ? (
          <div className="space-y-3">
            {generatedReports.map((report, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900 capitalize">{report.type} Report</p>
                    <p className="text-xs text-gray-600">
                      Generated at {new Date(report.timestamp).toLocaleTimeString()}
                      {report.dateRange.startDate && ` • ${report.dateRange.startDate} to ${report.dateRange.endDate}`}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-12">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="font-medium text-gray-700">No reports generated yet</p>
            <p className="text-sm text-gray-500 mt-1">Click on any report card above to generate</p>
          </div>
        )}
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-900 font-medium">Report Generation Tips</p>
            <ul className="text-sm text-blue-800 mt-2 space-y-1 list-disc list-inside">
              <li>Leave date range empty to generate reports for all time</li>
              <li>Reports are generated in real-time based on current data</li>
              <li>Large date ranges may take longer to process</li>
              <li>Reports download automatically as JSON files to your computer</li>
              <li>Check the "Generated Reports" section above to see completed downloads</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;